import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { TwitterPicker, ColorResult } from "react-color";
import { widgetDatasetService } from "/app/src/services";
import { WidgetDataset } from "/app/src/models";
import DeleteButton from "/app/src/components/generic/components/buttons/DeleteButton";
import { handlePromiseError } from "/app/src/helpers/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Dataset component renders a dataset item.
 * @param {Object} props - The component props.
 * @param {WidgetDataset} props.dataset - The dataset to display.
 * @returns {JSX.Element} The rendered dataset component.
 */
export default function Dataset({ dataset }: { dataset: WidgetDataset }) {
  const [colourOpen, setColourOpen] = useState(false);

  /**
   * Toggles the state of the colour picker.
   */
  const toggleColour = () => {
    setColourOpen((prev) => !prev);
  };
  const queryClient = useQueryClient();

  const { mutateAsync: deleteDataset } = useMutation({
    mutationFn: (dataset: WidgetDataset) => {
      return widgetDatasetService
        .deleteSingle(dataset.id)
        .then(handlePromiseError);
    },
    onSuccess: () => {
      queryClient.setQueryData(
        ["widgetDatasets", dataset.widgetId],
        (oldData: { widget_datasets: WidgetDataset[] }) => {
          queryClient.invalidateQueries(["widgetData", dataset.widgetId]);
          return {
            widget_datasets: oldData.widget_datasets.filter(
              (d) => d.id !== dataset.id,
            ),
          };
        },
      );
    },
  });
  const { mutateAsync: updateDataset } = useMutation({
    mutationFn: (updatedDataset: WidgetDataset) => {
      return widgetDatasetService
        .updateSingle(updatedDataset.id, updatedDataset)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["widgetDatasets", dataset.widgetId],
        (oldData: { widget_datasets: WidgetDataset[] }) => {
          queryClient.invalidateQueries(["widgetData", dataset.widgetId]);
          return {
            widget_datasets: oldData.widget_datasets.map((dataset) => {
              if (dataset.id === response.widget_dataset.id) {
                return response.widget_dataset;
              }
              return dataset;
            }),
          };
        },
      );
    },
  });

  /**
   * Updates the dataset colour and closes the colour picker.
   * @param {ColorResult} colour - The selected colour result.
   */
  const updateColour = useCallback(
    (colour: ColorResult) => {
      updateDataset({ id: dataset.id, colour: colour.hex });
      setColourOpen(false);
    },
    [dataset.id, updateDataset],
  );

  /**
   * Handles the delete button click by deleting the dataset.
   */
  const handleDeleteClick = useCallback((): void => {
    deleteDataset(dataset);
  }, [dataset, deleteDataset]);

  /**
   * Handles the colour picker toggle button click.
   * @returns {void}
   */
  const toggleColourHandler = useCallback(() => {
    toggleColour();
  }, []);

  return (
    <div className="dataset flex justify-between items-center">
      <Link
        className="inline-flex items-center"
        to={`/reports/${dataset.Snapshot.reportId}/snapshots`}
        title={`${dataset.Snapshot.name} (${dataset.Snapshot.reportName})`}
      >
        <span className="text-[16px]">
          {dataset.Snapshot.name}
          <span className="italic ml-1">({dataset.Snapshot.reportName})</span>
        </span>
      </Link>

      <div className="flex items-center gap-2">
        <div className="colorPicker p-2">
          <button
            className="colorButton"
            onClick={toggleColourHandler}
            style={{ background: dataset.colour }}
          />

          {colourOpen && (
            <div className="popover">
              <TwitterPicker
                triangle="top-right"
                color={dataset.colour}
                onChangeComplete={updateColour}
              />
            </div>
          )}
        </div>

        <DeleteButton onClick={handleDeleteClick} />
      </div>
    </div>
  );
}
